import { useState } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import {
  ReviewDetailsCard,
  ReviewDetailsItem,
} from '@/features/review-details';

import { cn, getValueWithCurrency, openModal } from '@/shared/lib';
import { Button, Typography } from '@/shared/ui';

import { ExchangeFormValues } from '../model/schema';
import {
  useCreateExchangePaymentMutation,
  useExchangeFxQuoteMutation,
} from '../model/exchange';
import { useExchangeSteps } from '../model/useExchangeSteps';
import { RateModal } from './rate-modal';
import { useCompanyIdStore } from '@/entities/user';

interface ExchangeReviewContent {
  onClose: VoidFunction;
  className?: string;
}
export const ExchangeReviewContent = ({
  onClose,
  className,
}: ExchangeReviewContent) => {
  const { setStep } = useExchangeSteps();
  const { control, setValue } = useFormContext<ExchangeFormValues>();
  const companyId = useCompanyIdStore((state) => state.companyId);
  const fromCurrency = useWatch({ control, name: 'fromCurrency' });
  const toCurrency = useWatch({ control, name: 'toCurrency' });
  const fromAmount = useWatch({ control, name: 'fromAmount' });
  const toAmount = useWatch({ control, name: 'toAmount' });
  const rate = useWatch({ control, name: 'rate' });
  const fee = useWatch({ control, name: 'fee' });
  const lastEditedSide = useWatch({ control, name: 'lastEditedSide' });
  const [errorMessage, setErrorMessage] = useState('');
  const [quoteRate, setQuoteRate] = useState<number>(0);
  const { mutateAsync: getExchangeQuote, isPending: isGettingQuote } =
    useExchangeFxQuoteMutation();
  const { mutateAsync: createExchangePayment, isPending: isCreatingExchange } =
    useCreateExchangePaymentMutation();

  const isExpired = (expiresAt?: string | null) => {
    const expiresAtMs = new Date(expiresAt || '').getTime();
    return Number.isNaN(expiresAtMs) || Date.now() >= expiresAtMs;
  };
  const isNumber = (value: unknown) => typeof value === 'number' && Number.isFinite(value);
  const toNumber = (value: unknown) => {
    const next = Number(value);
    return Number.isFinite(next) ? next : null;
  };

  const openRateModal = () => {
    openModal({
      id: 'exchange-rate-modal',
      component: () => (
        <RateModal
          from={fromCurrency?.code || 'EUR'}
          to={toCurrency?.code || 'USD'}
          modalId="exchange-rate-modal"
        />
      ),
    });
  };

  const onSubmit = async () => {
    if (!fromCurrency?.virtualId || !fromCurrency?.code || !toCurrency?.code) {
      setErrorMessage('Please select both currencies');
      return;
    }
    if (!Number.isFinite(fromAmount) || fromAmount <= 0) {
      setErrorMessage('Please enter an amount to convert');
      return;
    }
    if (!fromCurrency?.virtualId || !fromCurrency?.currencyId || !toCurrency?.virtualId || !toCurrency?.currencyId) {
      setErrorMessage('Missing currency details');
      return;
    }

    try {
      setErrorMessage('');

      const quote = await getExchangeQuote({
        sourceAmount: fromAmount.toString(),
        sourceCurrencyTicker: fromCurrency.code,
        targetCurrencyTicker: toCurrency.code,
        virtualAccountId: fromCurrency.virtualId,
      });
      if (isExpired(quote.expiresAt)) {
        setErrorMessage('Quote has expired. Please request it again.');
        setQuoteRate(0);
        return;
      }

      const nextRate = toNumber(quote.exchangeRate);
      if (nextRate === null) {
        setErrorMessage('Could not get exchange rate. Please request it again.');
        setQuoteRate(0);
        return;
      }
      setQuoteRate(nextRate);
      setValue('rate', nextRate);

      const nextToAmount = toNumber(quote.targetAmount);
      const nextToAmountValue = isNumber(nextToAmount)
        ? Number(nextToAmount.toFixed(2))
        : isNumber(toAmount)
          ? toAmount
          : 0;
      setValue('toAmount', nextToAmountValue);

      const paymentInput = {
        senderAccountId: fromCurrency.virtualId,
        recipientAccountId: toCurrency.virtualId,
        sourceCurrencyId: fromCurrency.currencyId,
        targetCurrencyId: toCurrency.currencyId,
        recipientClientId: companyId || '',
        quoteId: quote.id,
      };

      if (lastEditedSide === 'to') {
        paymentInput.targetAmount = toAmount.toString();
      } else {
        paymentInput.sourceAmount = fromAmount.toString();
      }

      const { vabsCreateMyExchangePayment } = await createExchangePayment(paymentInput);
      if (vabsCreateMyExchangePayment?.id) {
        setStep('success');
        return;
      }

      setErrorMessage('Failed to create exchange payment.');
    } catch (e) {
      console.log(e);
      setErrorMessage('Something went wrong. Please try again.');
      setQuoteRate(0);
    }
  };

  const displayedRate = isNumber(quoteRate) && quoteRate > 0 ? quoteRate : rate;
  const displayedRateLabel = isNumber(displayedRate) ? displayedRate.toFixed(2) : '--';
  const isSubmitting = isGettingQuote || isCreatingExchange;
  return (
    <div className={cn('flex w-full flex-grow flex-col', className)}>
      <ReviewDetailsCard onEdit={onClose} title="YOUR EXCHANGE">
        <ReviewDetailsItem
          title="You're converting"
          value={getValueWithCurrency(fromAmount, fromCurrency.symbol)}
        />
        <ReviewDetailsItem
          title="To"
          value={getValueWithCurrency(toAmount, toCurrency.symbol)}
        />
        <ReviewDetailsItem
          title="Price"
          value={`${getValueWithCurrency(1, fromCurrency.symbol)} = ${getValueWithCurrency(displayedRateLabel, toCurrency.symbol)}`}
          linkLabel={`${getValueWithCurrency(1, fromCurrency.symbol)} = ${getValueWithCurrency(displayedRateLabel, toCurrency.symbol)}`}
          onLinkClick={openRateModal}
        />
        <ReviewDetailsItem
          title="Total fees (included)"
          value={getValueWithCurrency(fee.toFixed(2), fromCurrency.symbol)}
        />
        <ReviewDetailsItem
          title="Estimated total proceeds"
          value={getValueWithCurrency(toAmount.toFixed(2), toCurrency.symbol)}
        />
      </ReviewDetailsCard>

      <div className="mt-10 flex flex-col gap-y-6">
        {/* <Typography
          variant="14-r"
          color="text-base-100"
          className="text-center"
        >
          Including a {fee.toFixed(2)}&#8239;{fromCurrency.symbol} fee
        </Typography> */}
        {!!errorMessage && (
          <Typography variant="14-r" className="text-negative-500">
            {errorMessage}
          </Typography>
        )}
        <Button
          variant="brand-500"
          size="d-l"
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={onSubmit}
        >
          Convert {fromAmount}&#8239;{fromCurrency.symbol}
        </Button>
      </div>
    </div>
  );
};
